import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { getRelativeRange } from "../hooks/useHistoricalData";
import ApexGraph from "./ApexGraph";
import HeatmapChart from "./HeatmapChart";
import QcpSummary from "./QcpSummary";
import FieldHelp from "../help/FieldHelp";
import { getClientMachines } from "../controllers/machineController";
import { buildMachineColorMap } from "../utils/machineColors";
import { buildNodeSettingsLink } from "../utils/nodeSettingsLink";

const environmentHistoricalCache = new Map();

// No SECOND bucket: temperature barely moves in 3s and a week of second-buckets
// would be ~600k points.
const MODES = ["minute", "hour", "day"];

// Heatmap grid matches the energy one: Mon-Sun rows x 24 hourly columns.
const DAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const HOURS_LABELS = Array.from(
  { length: 24 },
  (_, i) => `${String(i).padStart(2, "0")}:00`,
);

// The API returns both series per sensor: C = temperature, RH = humidity.
const METRICS = {
  C: { label: "Temp (°C)", unit: "°C", heatType: "temperature", decimals: 1 },
  RH: { label: "Humidity (%)", unit: "%", heatType: "humidity", decimals: 1 },
};

// The API sends plain PKT wall-clock strings with no timezone marker, exactly like
// the energy /data does. new Date("2026-09-02") would be parsed as UTC and shift every
// bucket by the local offset (midnight -> 05:00 in PKT), so build the Date from parts.
const parseLocalTimestamp = (raw) => {
  const m = String(raw).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (!m) return null;
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] || 0),
    Number(m[5] || 0),
    Number(m[6] || 0),
  );
};

const pad2 = (n) => String(n).padStart(2, "0");

// value for <input type="datetime-local">
const formatDateTimeLocal = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

// what the API wants: plain local wall-clock, no timezone marker
const formatDateTimeForApi = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

const QUICK_RANGES = [
  { label: "1D", days: 1 },
  { label: "3D", days: 3 },
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
];

const quickRangeStyle = {
  fontSize: "11px",
  padding: "6px 12px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  cursor: "pointer",
  fontWeight: 700,
  color: "#475569",
  transition: "all 0.2s",
};

const selectActionStyle = {
  fontSize: "9px",
  padding: "2px 8px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  cursor: "pointer",
  fontWeight: 700,
  color: "#475569",
  transition: "all 0.2s",
};

const pillGroupStyle = {
  display: "flex",
  background: "#f1f5f9",
  padding: 2,
  borderRadius: 8,
};

const pillButtonStyle = (active) => ({
  padding: "4px 10px",
  borderRadius: 6,
  border: "none",
  backgroundColor: active ? "#fff" : "transparent",
  color: active ? "#0f172a" : "#64748b",
  fontSize: "10px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  textTransform: "uppercase",
});

const EnvironmentHistorical = ({ username }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("analysis"); // analysis | heatmap
  const [mode, setMode] = useState("hour");
  const [chartType, setChartType] = useState("line");
  const [metric, setMetric] = useState("C"); // C | RH
  // Energy's own range rule (full days ending yesterday), so the default is
  // exactly what the 7D chip produces and both tabs show the same dates.
  const defaultRange = () => getRelativeRange(7);
  // draftRange = what the inputs show, dateRange = what has actually been fetched.
  // Only Apply copies draft -> active, so typing a date never fires a request.
  const [dateRange, setDateRange] = useState(defaultRange);
  const [draftRange, setDraftRange] = useState(defaultRange);

  const [payload, setPayload] = useState(null);
  // Node Settings is the source of truth for a room's colour and its QCP
  // limits. /client-machines is the very response the Settings page builds its
  // colour map from (and it is cached), so resolving the line colour here from
  // the same list -- rather than from the fallback /environment-data invents
  // for a room with no colour saved -- is what makes the line match the swatch
  // on the card.
  const [machineMeta, setMachineMeta] = useState({ colorMap: {}, metaByName: {} });
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const startStr = formatDateTimeForApi(dateRange.start);
  const endStr = formatDateTimeForApi(dateRange.end);
  const draftStartStr = formatDateTimeLocal(draftRange.start);
  const draftEndStr = formatDateTimeLocal(draftRange.end);

  const isRangeDirty =
    draftStartStr !== formatDateTimeLocal(dateRange.start) ||
    draftEndStr !== formatDateTimeLocal(dateRange.end);
  const isRangeValid =
    !!draftStartStr && !!draftEndStr && draftRange.start <= draftRange.end;
  const canApply = isRangeDirty && isRangeValid;

  // Apply is for the dates only -- they are the one filter that needs a fetch.
  // Room and metric moved down to the chart header and take effect at once:
  // every room's temperature *and* humidity arrive in the same response, so
  // switching either is pure client-side work with nothing to wait for.
  const applyRange = () => {
    if (!canApply) return;
    setDateRange({ start: draftRange.start, end: draftRange.end });
  };

  // Quick ranges only fill the date boxes; Apply fetches, same as energy.
  const pickQuickRange = (days) => setDraftRange(getRelativeRange(days));


  const fetchData = useCallback(async () => {
    if (!username || !startStr || !endStr) return;
    setLoading(true);
    setError(null);
    try {
      const params = { username, mode, start: startStr, end: endStr };
      const queryString = new URLSearchParams(params).toString();

      if (environmentHistoricalCache.has(queryString)) {
        setPayload(environmentHistoricalCache.get(queryString));
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        const res = await api.get("/environment-data", {
          params,
        });
        environmentHistoricalCache.set(queryString, res.data || null);
        setPayload(res.data || null);
      }
    } catch (err) {
      console.error("Fetch environment-data error:", err);
      setError("Failed to load environment history.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [username, mode, startStr, endStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!username) return undefined;
    let cancelled = false;
    getClientMachines(username)
      .then((data) => {
        if (cancelled) return;
        // Same dedupe as the Settings page, so buildMachineColorMap is handed
        // an identical list and hands back identical colours.
        const seen = new Set();
        const cleaned = (data.machines || []).filter((m) => {
          if (seen.has(m.machine_id)) return false;
          seen.add(m.machine_id);
          return true;
        });
        const metaByName = {};
        cleaned.forEach((m) => {
          if (!m.machine_name) return;
          metaByName[m.machine_name] = {
            // Carried so the gear below the chart can deep-link to this
            // room's own card; names are not unique across meters.
            meterId: m.meter_id,
            standard: m.standard_temp,
            lcl: m.lcl_temp,
            lal: m.lal_temp,
            ual: m.ual_temp,
            ucl: m.ucl_temp,
          };
        });
        setMachineMeta({ colorMap: buildMachineColorMap(cleaned), metaByName });
      })
      .catch(() => {
        // Non-fatal: the chart keeps the colour the data endpoint supplied and
        // simply draws no limit lines.
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  // Loading progress ticker (smoothly increments from 15% to 100% based on active loading)
  useEffect(() => {
    let progressInterval = null;

    if (loading) {
      setLoadingProgress((prev) => (prev > 0 && prev < 90 ? prev : 15));
      const targetProgress = 88;

      progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev < targetProgress) {
            return Math.min(
              targetProgress,
              prev + Math.max(1, Math.ceil((targetProgress - prev) * 0.3))
            );
          }
          if (prev < 92) {
            return prev + 0.4;
          }
          return prev;
        });
      }, 100);
    } else {
      setLoadingProgress(100);
      const timeout = setTimeout(() => {
        setLoadingProgress(0);
      }, 350);
      return () => clearTimeout(timeout);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [loading]);

  const labels = useMemo(() => payload?.["Date & Time"] || [], [payload]);

  const sensors = useMemo(() => {
    const machines = payload?.machines || {};
    return Object.keys(machines).map((key) => {
      const name = machines[key].name || key;
      return {
        key,
        name,
        // Node Settings wins; the endpoint's own colour is only a stand-in
        // until the machine list lands.
        color: machineMeta.colorMap[name] || machines[key].color || "#1BA08E",
        data: machines[key].data || {},
      };
    });
  }, [payload, machineMeta]);

  // Default to the first sensor only - 11 overlaid lines is unreadable, and a single
  // series is what makes the Max/Average/Min lines meaningful.
  useEffect(() => {
    if (!sensors.length) return;
    setSelectedKeys((prev) => {
      const stillValid = prev.filter((k) => sensors.some((s) => s.key === k));
      return stillValid.length ? stillValid : [sensors[0].key];
    });
  }, [sensors]);

  const selectedSensors = useMemo(
    () => sensors.filter((s) => selectedKeys.includes(s.key)),
    [sensors, selectedKeys],
  );

  // The dropdown sensor is primary: chips can add others around it, but it can
  // never be deselected (same rule as the energy graph). selectedKeys is the
  // only state behind it, so the drop-down and the chips cannot drift apart.
  const primaryKey = selectedKeys[0] || "";

  const toggleSensor = (key) => {
    if (key === primaryKey) return;
    // Heatmap shows one sensor, so a chip click there swaps the selection
    // instead of adding to it, and the dropdown follows.
    if (viewMode === "heatmap") {
      setSelectedKeys([key]);
      return;
    }
    // In Analysis a chip only adds a line around the primary.
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  useEffect(() => {
    if (viewMode !== "heatmap") return;
    setSelectedKeys((prev) => (prev.length > 1 ? [prev[0]] : prev));
    // A Day x Hour grid is only fillable by hourly buckets: DAY gives one point per
    // day (a single column) and MINUTE gives 60x more points than there are cells.
    setMode("hour");
  }, [viewMode]);

  const handleSelectAll = () => setSelectedKeys(sensors.map((s) => s.key));
  const handleSelectNone = () =>
    // keep the primary, exactly like energy where it stays active
    setSelectedKeys(primaryKey ? [primaryKey] : []);

  const metricMeta = METRICS[metric];

  const datasets = useMemo(
    () =>
      selectedSensors.map((s) => ({
        label: s.name,
        data: s.data[metric] || [],
        borderColor: s.color,
      })),
    [selectedSensors, metric],
  );

  const valueFormatter = useCallback(
    (value) =>
      value === null || value === undefined || Number.isNaN(Number(value))
        ? "-"
        : `${Number(value).toFixed(metricMeta.decimals)} ${metricMeta.unit}`,
    [metricMeta],
  );

  const [showStats, setShowStats] = useState(false);

  // Reference lines for a single series.
  //
  // Max / Average / Min are measured off the points on screen. "Average" is
  // the median, not the mean: a room spends most of its time near its working
  // temperature and the rest in defrost or door-open spikes, and a mean is
  // dragged towards those spikes while the median reports the temperature
  // actually held. The label stays "Average" because that is what the people
  // reading this chart call it.
  //
  // The QCP limits come from Node Settings and describe temperature, so they
  // are left off the humidity series.
  const statLines = useMemo(() => {
    if (datasets.length !== 1) return [];
    const values = (datasets[0].data || [])
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return [];

    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median =
      sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;

    const lines = showStats
      ? [
        { label: "Max", value: sorted[sorted.length - 1], color: "#475569", dashArray: 8 },
        { label: "Average", value: median, color: "#2563eb", dashArray: 8 },
        { label: "Min", value: sorted[0], color: "#94a3b8", dashArray: 8 },
      ]
      : [];

    if (metric !== "C") return lines;

    const limits = machineMeta.metaByName[selectedSensors[0]?.name] || {};
    // UCL is derived from the upper amber limit, so the two normally hold the
    // same value. Group by value and merge the labels rather than stacking two
    // identical lines with overlapping captions.
    const byValue = new Map();
    [
      // Limit captions sit inset from the right edge, with Max/Average/Min
      // flush against it: Max and Upper amber (or Average and Lower amber)
      // often land on the same value, and two labels at the same height in the
      // same place hide one another.
      { label: "LCL", value: limits.lcl, color: "#dc2626", dashArray: 6, labelOffsetX: -86 },
      { label: "Lower amber", value: limits.lal, color: "#f59e0b", dashArray: 4, labelOffsetX: -86 },
      { label: "Upper amber", value: limits.ual, color: "#f59e0b", dashArray: 4, labelOffsetX: -86 },
      { label: "UCL", value: limits.ucl, color: "#dc2626", dashArray: 6, labelOffsetX: -86 },
      { label: "Standard", value: limits.standard, color: "#16a34a", dashArray: 0, labelOffsetX: -86 },
    ].forEach((line) => {
      const value = Number(line.value);
      if (!Number.isFinite(value)) return;
      const existing = byValue.get(value);
      if (existing) existing.label += ` / ${line.label}`;
      else byValue.set(value, { ...line, value });
    });

    return [...lines, ...byValue.values()];
  }, [datasets, showStats, metric, machineMeta, selectedSensors]);

  // A Day x Hour grid can only represent ONE series, exactly like the energy heatmap
  // (which is driven by a single machine). Averaging sensors is not an option here --
  // mixing a freezer (-29 C) with a dough room (+26 C) produces a meaningless number.
  const heatmapSensor = selectedSensors[0] || null;
  const heatmapLimits = machineMeta.metaByName[heatmapSensor?.name] || null;

  // Temperature heatmap uses amber limit colors (Green = Within Range, Amber = Near Limit, Red = Out of Range)
  // rather than the machine's individual color. Humidity retains its light-to-dark ramp.
  const heatmapColor =
    metric === "C"
      ? "amberLimits"
      : heatmapSensor?.color && /^#[0-9a-f]{6}$/i.test(heatmapSensor.color)
        ? heatmapSensor.color
        : "multi";

  const heatmap = useMemo(() => {
    const sums = DAY_LABELS.map(() => Array(24).fill(0));
    const counts = DAY_LABELS.map(() => Array(24).fill(0));

    labels.forEach((raw, idx) => {
      const d = parseLocalTimestamp(raw);
      if (!d || Number.isNaN(d.getTime())) return;
      // JS: Sunday=0 -> our grid starts on Monday
      const dayIdx = (d.getDay() + 6) % 7;
      const hour = d.getHours();

      const v = Number((heatmapSensor?.data[metric] || [])[idx]);
      if (!Number.isFinite(v)) return;
      sums[dayIdx][hour] += v;
      counts[dayIdx][hour] += 1;
    });

    let min = null;
    let max = null;
    const rows = DAY_LABELS.map((label, dayIdx) => ({
      label,
      values: Array.from({ length: 24 }, (_, h) => {
        if (!counts[dayIdx][h]) return null;
        const v = sums[dayIdx][h] / counts[dayIdx][h];
        min = min === null || v < min ? v : min;
        max = max === null || v > max ? v : max;
        return v;
      }),
    }));

    return { rows, min, max };
  }, [labels, heatmapSensor, metric]);

  const hasData = labels.length > 0 && sensors.length > 0;

  return (
    <>
      {loading && (
        <div className="historical-global-loader-overlay" role="status" aria-live="polite">
          <div className="historical-global-loader-content">
            <div className="historical-lightning-loader" aria-hidden="true">
              <svg viewBox="0 0 160 247" focusable="false">
                <defs>
                  <linearGradient
                    id="envHistoricalProgressGradient"
                    x1="80"
                    y1="241.5"
                    x2="80"
                    y2="5.5"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset={`${Math.min(100, Math.max(5, loadingProgress))}%`} stopColor="#009F8D" stopOpacity="1" />
                    <stop offset={`${Math.min(100, Math.max(5, loadingProgress))}%`} stopColor="#009F8D" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#009F8D" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  className="historical-bolt-shape"
                  fill="url(#envHistoricalProgressGradient)"
                  id="env-historical-shape"
                  stroke="#009F8D"
                  strokeWidth="8"
                  strokeLinejoin="round"
                  d="M137.641 5.5H60.1789L5.5 135.194H67.9251L32.8394 241.5L154.5 86.718H98.9098L137.641 5.5Z"
                />
              </svg>
            </div>
            <div className="historical-loader-percent-badge">
              <span className="historical-loader-percent-number">
                {Math.round(loadingProgress)}%
              </span>
              <span className="historical-loader-percent-label">
                Loading Historical Data...
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ===== Filter bar: its own card, like the energy Historical bar ===== */}
      <div className="env-heatmap-section">
        <div
          className="env-heatmap-controls"
          style={{
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            // the class sets space-between; everything here packs to the left
            justifyContent: "flex-start",
            borderBottom: "none",
            paddingBottom: 0,
          }}
        >
          <div className="env-datetime-group">
            <input
              type="datetime-local"
              step="1"
              className="env-date-input"
              value={draftStartStr}
              onChange={(e) => {
                const val = new Date(e.target.value);
                if (!Number.isNaN(val.getTime()))
                  setDraftRange((p) => ({ ...p, start: val }));
              }}
            />
            <span className="env-date-separator">
              <FieldHelp id="environment.historicalDateRange">TO</FieldHelp>
            </span>
            <input
              type="datetime-local"
              step="1"
              className="env-date-input"
              value={draftEndStr}
              onChange={(e) => {
                const val = new Date(e.target.value);
                if (!Number.isNaN(val.getTime()))
                  setDraftRange((p) => ({ ...p, end: val }));
              }}
            />
          </div>

          {/* Quick ranges apply straight away, like the energy 1D/3D/7D/30D buttons */}
          <div style={{ display: "flex", gap: 6 }}>
            {QUICK_RANGES.map((q) => (
              <button
                key={q.days}
                type="button"
                onClick={() => pickQuickRange(q.days)}
                style={quickRangeStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Room and metric are not here: they need no fetch, so they sit with
            the chart instead and apply on click. */}

          <button
            type="button"
            onClick={applyRange}
            disabled={!canApply}
            title={isRangeDirty && !isRangeValid ? "From must be on or before To" : undefined}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              background: canApply ? "#1BA08E" : "#e2e8f0",
              color: canApply ? "#fff" : "#94a3b8",
              fontSize: 13,
              fontWeight: 700,
              cursor: canApply ? "pointer" : "not-allowed",
            }}
          >
            Apply
          </button>
        </div>
      </div>

      {/* QCP table + chart - hides itself when no sensor has limits configured */}
      <QcpSummary username={username} startDate={startStr} endDate={endStr} />

      {/* ===== Graph card ===== */}
      <div className="env-heatmap-section">
        {/* ===== Chart header: how to view it ===== */}
        <div
          className="env-heatmap-controls"
          style={{ flexWrap: "wrap", gap: 10, justifyContent: "space-between" }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              flexShrink: 0,
              visibility: viewMode === "analysis" ? "visible" : "hidden",
            }}
          >
            <button
              type="button"
              onClick={handleSelectAll}
              style={selectActionStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
            >
              ALL
            </button>
            <button
              type="button"
              onClick={handleSelectNone}
              style={selectActionStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#f8fafc")}
            >
              NONE
            </button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginLeft: "auto",
            }}
          >
            {/* Room picker. selectedKeys is the only state behind it, so choosing
              here replaces the selection and the chips below follow at once. */}
            <FieldHelp id="environment.historicalSensor">
              <select
                value={primaryKey}
                onChange={(e) => setSelectedKeys([e.target.value])}
                className="env-date-input"
                style={{ cursor: "pointer", minWidth: 180 }}
                disabled={!sensors.length}
              >
                {sensors.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name}
                  </option>
                ))}
              </select>
            </FieldHelp>

            {/* Temp / Humidity toggle. Both series are already in the payload, so
              this is an instant switch rather than another dropdown + Apply. */}
            <FieldHelp id="environment.historicalMetric" inline={false}>
              <div style={pillGroupStyle}>
                {Object.keys(METRICS).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setMetric(key)}
                    style={pillButtonStyle(metric === key)}
                  >
                    {METRICS[key].label}
                  </button>
                ))}
              </div>
            </FieldHelp>

            <FieldHelp id="environment.historicalView" inline={false}>
              <div style={pillGroupStyle}>
                {[
                  { id: "analysis", label: "Analysis", icon: "📈" },
                  { id: "heatmap", label: "Heatmap", icon: "🌡️" },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setViewMode(v.id)}
                    style={pillButtonStyle(viewMode === v.id)}
                  >
                    <span>{v.icon}</span>
                    {v.label}
                  </button>
                ))}
              </div>
            </FieldHelp>

            <FieldHelp id="environment.historicalAggregation" inline={false}>
              <div style={pillGroupStyle}>
                {MODES.map((m) => {
                  const locked = viewMode === "heatmap" && m !== "hour";
                  return (
                    <button
                      key={m}
                      onClick={() => !locked && setMode(m)}
                      disabled={locked}
                      title={locked ? "Heatmap needs hourly buckets" : undefined}
                      style={{
                        ...pillButtonStyle(mode === m),
                        cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.4 : 1,
                      }}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </FieldHelp>

            {viewMode === "analysis" && (
              <div style={pillGroupStyle}>
                {["line", "bar"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setChartType(t)}
                    style={pillButtonStyle(chartType === t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {viewMode === "analysis" && (
              <select
                value={showStats ? "with_stats" : "limits_only"}
                onChange={(e) => setShowStats(e.target.value === "with_stats")}
                className="env-date-input"
                style={{
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#1e293b",
                  background: "#f8fafc",
                  borderColor: "#cbd5e1",
                }}
                title="Toggle Min / Max / Avg range lines"
                aria-label="Reference lines selection"
              >
                <option value="with_stats">Show Min, Max, Avg</option>
                <option value="limits_only">Limits Only (UCL/LCL)</option>
              </select>
            )}
          </div>
        </div>

        {/* ===== Chart / Heatmap ===== */}
        <div className="env-heatmap-chart-wrapper">
          {loading && !payload ? (
            <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
              Loading history...
            </div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: "center", color: "#dc2626" }}>{error}</div>
          ) : !hasData ? (
            <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
              No data for this range
            </div>
          ) : viewMode === "analysis" ? (
            <ApexGraph
              id="environment-analysis-chart"
              labels={labels}
              rawLabels={labels}
              datasets={datasets}
              statLines={statLines}
              chartType={chartType}
              valueFormatter={valueFormatter}
              showBrush
              mode={mode}
              metricUnit={metricMeta.unit}
            />
          ) : (
            <HeatmapChart
              rows={heatmap.rows}
              timeLabels={HOURS_LABELS}
              minValue={heatmap.min}
              maxValue={heatmap.max}
              loading={loading}
              metricLabel={`${heatmapSensor?.name || ""}: ${metricMeta.label}`}
              valueFormatter={valueFormatter}
              machineColor={heatmapColor}
              rowHeaderLabel="Day"
              metricType={metricMeta.heatType}
              limits={metric === "C" ? heatmapLimits : null}
            />
          )}
        </div>

        {/* ===== Sensor chips ===== */}
        {sensors.length > 0 && (
          <div style={{ padding: "12px 4px 4px" }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
              {viewMode === "heatmap" ? (
                "Heatmap shows one sensor. Pick a chip."
              ) : (
                <span
                  style={{ display: "inline-flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}
                >
                  <span>
                    <FieldHelp id="environment.historicalSelectedSensors">Selected Sensors</FieldHelp>
                    {`: ${selectedSensors.length}`}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: selectedSensors[0]?.color || "#1BA08E",
                        border: "1px solid #e2e8f0",
                        display: "inline-block",
                      }}
                    />
                    <FieldHelp id="environment.historicalSeriesColor">
                      Line colour from Node Settings
                    </FieldHelp>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          buildNodeSettingsLink({
                            meterId: machineMeta.metaByName[selectedSensors[0]?.name]?.meterId,
                            machineName: selectedSensors[0]?.name,
                          }),
                        )
                      }
                      title="Change this room's colour and limits in Node Settings"
                      aria-label="Change this room's colour and limits in Node Settings"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 2,
                        color: "#94a3b8",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#1BA08E"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#94a3b8"; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </button>
                  </span>
                  {metric === "C" && (
                    <FieldHelp id="environment.historicalReferenceLines">
                      Reference lines
                    </FieldHelp>
                  )}
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sensors.map((s) => {
                const active = selectedKeys.includes(s.key);
                const isPrimary = s.key === primaryKey;
                return (
                  <button
                    key={s.key}
                    disabled={isPrimary}
                    onClick={() => toggleSensor(s.key)}
                    title={
                      isPrimary
                        ? `${s.name} is the primary selected sensor and cannot be deselected`
                        : `Toggle ${s.name}`
                    }
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      border: `1.5px solid ${active ? s.color : "#e2e8f0"}`,
                      background: active ? `${s.color}1a` : "#fff",
                      color: active ? "#0f172a" : "#64748b",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: isPrimary ? "not-allowed" : "pointer",
                      opacity: isPrimary ? 0.85 : 1,
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {s.name}
                    {isPrimary && (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: 2, opacity: 0.7 }}
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default EnvironmentHistorical;
